import * as React from "react";
import {useEffect, useState} from "react";
import {Link, required, SaveButton, SelectInput, SimpleForm, TextInput, useNotify, useRedirect} from "react-admin";
import {useParams} from "react-router-dom";
import {useDispatch} from "react-redux";
import {useFormContext, useWatch} from "react-hook-form";
import {httpCustomEdit, httpGet} from "../../../Api/api";
import setLoadStatus from "../../../Store/LoaderReducer";

const LINK_TYPE_CHOICES = [
    {id: 'dare_advanced_call', name: 'Dare Advanced Call'},
    {id: 'webinar', name: 'Webinar'},
    {id: 'dare_academy_call', name: 'Dare Academy Call'},
    {id: 'external_event', name: 'External Event'},
];

const EXTERNAL_URL_LINK_TYPES = [
    'dare_academy_call',
    'external_event',
];

const DEFAULT_LINK_TYPE = LINK_TYPE_CHOICES[0].id;

const EMPTY_RECORD = {
    id: null,
    link_type: DEFAULT_LINK_TYPE,
    external_url: '',
    button_text: '',
};

const normalizeButtonRecord = (data) => ({
    ...EMPTY_RECORD,
    ...data,
    link_type: data?.link_type || DEFAULT_LINK_TYPE,
    external_url: data?.external_url || '',
    button_text: data?.button_text || '',
});

const CalendarEventButtonToolbar = () => (
    <div className="customerActionToolbar edit-user-btns">
        <Link to="/calendar-events" className="button bar-purple-button">Cancel</Link>
        <SaveButton className="button bar-gradient-button" label="Save" alwaysEnable icon={<></>} />
    </div>
);

const EnsureDefaultLinkType = () => {
    const {getValues, setValue} = useFormContext();

    useEffect(() => {
        if (!getValues('link_type')) {
            setValue('link_type', DEFAULT_LINK_TYPE, {shouldDirty: false});
        }
    }, [getValues, setValue]);

    return null;
};

const CalendarEventButtonFields = () => {
    const linkType = useWatch({name: 'link_type'}) || DEFAULT_LINK_TYPE;
    const showExternalUrl = EXTERNAL_URL_LINK_TYPES.includes(linkType);

    return (
        <div className="tabbedFormWrap">
            <EnsureDefaultLinkType />
            <SelectInput
                className="formFields"
                label="Link Type"
                source="link_type"
                choices={LINK_TYPE_CHOICES}
                fullWidth
                validate={required()}
            />
            <p className="required-text">Required</p>
            {showExternalUrl && (
                <>
                    <TextInput
                        className="formFields"
                        label="External URL"
                        source="external_url"
                        fullWidth
                        validate={required()}
                    />
                    <p className="required-text">Required</p>
                </>
            )}
            <TextInput
                className="formFields"
                label="Button Text"
                source="button_text"
                fullWidth
                validate={required()}
            />
            <p className="required-text">Required</p>
        </div>
    );
};

export const ConfigureCalendarEventButton = () => {
    const {id} = useParams();
    const notify = useNotify();
    const redirect = useRedirect();
    const dispatch = useDispatch();
    const [record, setRecord] = useState(EMPTY_RECORD);
    const [formKey, setFormKey] = useState(0);

    useEffect(() => {
        httpGet(`calendar-events/${id}/button`)
            .then((response) => {
                setRecord(normalizeButtonRecord(response.data));
                setFormKey((current) => current + 1);
            })
            .catch(() => {
                setRecord(EMPTY_RECORD);
                setFormKey((current) => current + 1);
            });
    }, [id]);

    const handleSubmit = (data) => {
        dispatch(setLoadStatus(true));

        const linkType = data.link_type || record.link_type || DEFAULT_LINK_TYPE;
        const showExternalUrl = EXTERNAL_URL_LINK_TYPES.includes(linkType);

        httpCustomEdit(`calendar-events/${id}/button`, {
            link_type: linkType,
            external_url: showExternalUrl ? (data.external_url || '') : '',
            button_text: data.button_text || '',
        })
            .then(() => {
                notify(record?.id ? 'Calendar event button updated' : 'Calendar event button created');
                redirect('/calendar-events');
            })
            .catch((error) => {
                const message = error?.body?.message || error?.message || 'Failed to save button configuration';
                notify(message, {type: 'error'});
            })
            .finally(() => {
                dispatch(setLoadStatus(false));
            });
    };

    return (
        <div className="edit-customer">
            <h2 className="dailyTitle">Configure Button</h2>
            <SimpleForm
                key={formKey}
                record={record}
                onSubmit={handleSubmit}
                toolbar={<CalendarEventButtonToolbar />}
                sanitizeEmptyValues={false}
            >
                <CalendarEventButtonFields />
            </SimpleForm>
        </div>
    );
};
